import React, { useCallback, useEffect, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Step } from "Context/ActualInventoryContext";
import { useActualInventoryContext } from "Hooks/useActualInventoryContext";
import { useOrderContext } from "Hooks/useOrderContext";
import useYupValidation, { checkFieldsHasError } from "Hooks/useYupValidation";
import { ItemJob } from "Models/sales";
import { ItemJobToStorageBox, StorageBox } from "Models/storage.box";
import { MapCell } from "Models/warehouse.mapcell";
import WarehousingService, {
  PropsClear,
  UnloadItemJobInStorageBox,
} from "Services/warehousing.service";
import { canAddItemJobInLocation } from "Util/canAddItemJobInLocation";
import { checkError } from "Util/checkError";
import { isItemME } from "Util/isItemME";
import { Color } from "Components/Form/Button";
import { ActionButtonProps } from "Components/Generic/ActionButtons";
import Modal from "Components/Generic/Modal";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import UISteps from "Components/Generic/UISteps";
import UITabMaterial from "Components/Generic/UITabMaterial";
import MEChecklist from "./Checklist";
import Details, { ItemJobLocation } from "./Details";
import MoveConfirmationModal, { MoveConfirmationHandler } from "./Details/MoveConfirmationModal";
import ItemConditions from "./ItemConditions";
import Photos from "./Photos";
import useStyles from "./styles";
import { useActions } from "./useActions";

enum Location {
  NO_LOCATION = "no location",
  STORAGE_BOX = "storage box",
  WAREHOUSE = "warehouse",
}

interface Props {
  openModal: boolean;
  itemJobSelected?: ItemJob;
  storageBoxes?: StorageBox[];
  mustBeHardwareBox: boolean;
  orderStatus?: string;
  closeModal: () => void;
  onSubmit: (itemJob: ItemJob) => void;
  openOnlyComments?: boolean;
}

interface MoveItemJobProps {
  itemJob?: ItemJob;
  mapCell: MapCell;
}

const CreateActualItemModal = (props: Props) => {
  const classes = useStyles();
  const confirmationModalRef = useRef<MoveConfirmationHandler>(null);

  const {
    storageBoxes = [],
    openModal,
    mustBeHardwareBox,
    orderStatus,
    closeModal,
    onSubmit,
    openOnlyComments,
  } = props;

  const {
    currentStep,
    itemJob,
    uuidItemJob,
    nextStep,
    stepsOption,
    previousStep,
    errors,
    setErrors,
  } = useActualInventoryContext();

  const {
    mapCells,
    warehouseList,
    setMapId,
    isWarehouseListFetching,
    isMapCellsFetching,
    setBranchIds,
  } = useActions();

  const { order } = useOrderContext();

  const { t: translate } = useTranslation();

  const [selectedLeftPanel, setSelectedLeftPanel] = useState<number>(0);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const [itemJobLocation, setItemJobLocation] = useState<ItemJobLocation>();

  const [itemJobStatus, setItemJobStatus] = useState<string>("");
  const [location, setLocation] = useState<string>(Location.NO_LOCATION);
  const [calledErroOfME, setCalledErrorOfME] = useState(false);

  const { fieldHasError } = useYupValidation(errors || []);

  useEffect(() => {
    const branchIds: number[] = [];

    if (order?.branchFromId !== undefined) branchIds.push(order?.branchFromId);
    if (order?.toBranchId !== undefined) branchIds.push(order?.toBranchId);

    setBranchIds(branchIds);
  }, [order?.branchFromId, order?.toBranchId]);

  useEffect(() => {
    if (isSubmitted) validateForm();
    if (
      itemJob?.storageBoxes &&
      itemJob?.storageBoxes?.length &&
      itemJob?.storageBoxes?.[0].valid
    ) {
      setLocation(Location.STORAGE_BOX);
      setItemJobLocation(
        (state) =>
          ({
            ...(state || {}),
            storageBoxId: itemJob.storageBoxes?.[0].id || 0,
          } as ItemJobLocation)
      );
    } else if (itemJob?.mapCells && itemJob?.mapCells?.length && itemJob?.mapCells?.[0].valid) {
      const mapCells = itemJob?.mapCells?.[0];

      setLocation(Location.WAREHOUSE);
      setItemJobLocation((state) => ({
        ...(state || {}),
        warehouseId: Number(mapCells?.map?.warehouseId),
        sectionId: Number(mapCells?.map?.sectionId),
        mapId: Number(mapCells?.map?.id),
        mapCellId: Number(mapCells?.id),
      }));
    }
  }, [itemJob]);

  useEffect(() => {
    if (
      checkFieldsHasError(
        fieldHasError,
        "meOriginChecklist.meDone",
        "meOriginChecklist.meWorking"
      ) &&
      currentStep === Step.DETAILS &&
      !calledErroOfME
    ) {
      setCalledErrorOfME(true);
      NotificationToast.error(translate("meChecklistIsmandatoryForThisItemPleaseAnswerIt"), {
        id: "meChecklistIsmandatoryForThisItemPleaseAnswerIt",
      });
    }
  }, [currentStep, errors, calledErroOfME]);

  const validateForm = (fromNext = false) => {
    try {
      const itemME = isItemME(itemJob);
      const schema = yup.object().shape({
        itemME: yup.boolean(),
        roomId: yup
          .number()
          .nullable()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        description: yup
          .string()
          .nullable()
          .required(translate("thisFieldIsRequired"))
          .typeError(translate("thisFieldIsRequired")),
        // M&E validations
        make: yup
          .string()
          .nullable()
          .when("itemME", {
            is: true,
            then: yup
              .string()
              .required(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
          }),
        model: yup
          .string()
          .nullable()
          .when("itemME", {
            is: true,
            then: yup
              .string()
              .required(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
          }),
        serialNumber: yup
          .string()
          .nullable()
          .when("itemME", {
            is: true,
            then: yup
              .string()
              .required(translate("thisFieldIsRequired"))
              .typeError(translate("thisFieldIsRequired")),
          }),
        meOriginChecklist: yup
          .object()
          .shape({
            meDone: yup.string().nullable(),
            meWorking: yup.string().nullable(),
          })
          .when(["itemME", "fromNext", "currentStep"], {
            is: (_itemME: boolean, _fromNext: boolean, _currentStep: string) => {
              if (!_itemME) return false;
              if (_currentStep === Step.DETAILS) {
                if (_fromNext) {
                  return false;
                }

                return true;
              }

              return true;
            },
            then: yup.object().shape({
              meDone: yup
                .string()
                .required(translate("thisFieldIsRequired"))
                .typeError(translate("thisFieldIsRequired")),
              meWorking: yup
                .string()
                .nullable()
                .when("meDone", {
                  is: "yes",
                  then: yup
                    .string()
                    .required(translate("thisFieldIsRequired"))
                    .typeError(translate("thisFieldIsRequired")),
                }),
            }),
          }),
        isProGear: yup.boolean(),
        proGearBelongsTo: yup.string().when("isProGear", {
          is: true,
          then: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
          otherwise: yup.string().nullable(),
        }),
      });

      schema.validateSync(
        {
          fromNext,
          currentStep,

          roomId: itemJob?.roomId,
          description: itemJob?.description,
          itemJobBoxContents: itemJob?.itemJobBoxContents,
          packedInto: itemJob?.packedInto,

          itemME,
          make: itemJob?.make,
          model: itemJob?.model,
          serialNumber: itemJob?.serialNumber,
          meOriginChecklist: {
            meDone: itemJob?.meDone,
            meWorking: itemJob?.meWorking,
          },
          isProGear: itemJob?.isProGear || itemJob?.item?.isProGear,
          proGearBelongsTo: itemJob?.proGearBelongsTo,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors(err.inner);
      }

      return false;
    }
  };

  const renderModalTitle = () => {
    if (openOnlyComments === true) return translate("comments");
    if (uuidItemJob !== undefined) return translate("editItem");

    return translate("createNewItem");
  };

  const moveItemJob = (callback?: () => void) =>
    useMutation(
      ["moveItemJob"],
      ({ mapCell, itemJob }: MoveItemJobProps) => WarehousingService.movement({ mapCell, itemJob }),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          confirmationModalRef?.current?.close();

          const mapCell = mapCells?.find((cell) => cell.id === itemJobLocation?.mapCellId);

          onSubmit({
            ...itemJob,
            status: itemJobStatus,
            storageBoxes: [],
            mapCells: mapCell ? [mapCell] : [],
          });
          if (callback) callback();
        },
        onError: (err: AxiosError<Error>) => {
          checkError(err, translate("anErrorOccurredTryAgain"));
        },
      }
    );

  const moveItemToStorageBox = () =>
    useMutation(
      ["move.item.to.storage.box"],
      (data: ItemJobToStorageBox) => WarehousingService.moveItemJobToStorageBox(data),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          const storageBox =
            storageBoxes.find((storageBox) => storageBox.id === itemJobLocation?.storageBoxId) ||
            ({} as StorageBox);

          onSubmit({
            ...itemJob,
            status: itemJobStatus,
            storageBoxes: [storageBox],
          });
        },
      }
    );

  const removeItemJobInStorageBox = () =>
    useMutation(
      ["remove.item.in.storage.box"],
      (data: UnloadItemJobInStorageBox) => WarehousingService.unloadItemJobInStorageBox(data),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));

          onSubmit({
            ...itemJob,
            status: itemJobStatus,
            storageBoxes: [],
            mapCells: [],
          });

          setItemJobLocation({
            warehouseId: undefined,
            mapCellId: undefined,
            mapId: undefined,
            sectionId: undefined,
            storageBoxId: undefined,
          });
        },
      }
    );

  const removeItemJobInMapCell = () =>
    useMutation(
      ["remove.item.in.map.cell"],
      (data: PropsClear) => WarehousingService.clearMapCell(data),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));

          onSubmit({
            ...itemJob,
            status: itemJobStatus,
            mapCells: [],
            storageBoxes: [],
          });

          setItemJobLocation({
            warehouseId: undefined,
            mapCellId: undefined,
            mapId: undefined,
            sectionId: undefined,
            storageBoxId: undefined,
          });
        },
      }
    );

  const { mutate: mutateItemJob, isLoading: loadingMoveItemJob } = moveItemJob();
  const { mutate: moveToStorageBox, isLoading: loadingMoveItemJobToStorageBox } =
    moveItemToStorageBox();
  const { mutate: mutateRemoveIteJobInStorageBox, isLoading: loadingRemoveItemJobInStorageBox } =
    removeItemJobInStorageBox();

  const { mutate: mutateRemoveItemJobInMapCell, isLoading: loadingRemoveItemJobInMapCell } =
    removeItemJobInMapCell();

  const handleSubmit = () => {
    if (itemJobLocation?.mapCellId) {
      const mapCell = mapCells?.find((cell) => cell.id === itemJobLocation.mapCellId);

      if (mapCell && itemJob && itemJob.id !== undefined) {
        mutateItemJob({ itemJob, mapCell });
      } else onSubmit({ ...itemJob });
    }
  };

  const onSave = () => {
    setIsSubmitted(true);
    setCalledErrorOfME(false);

    const itemJobObj = { ...itemJob, status: itemJobStatus };

    if (validateForm()) {
      if (itemJobObj) {
        if (location === Location.NO_LOCATION) {
          if (
            itemJobObj?.id &&
            ((itemJobObj?.storageBoxes && itemJobObj?.storageBoxes?.length > 0) ||
              (itemJobObj?.mapCells &&
                itemJobObj?.mapCells?.length > 0 &&
                itemJobObj?.mapCells?.[0]?.id))
          ) {
            if (itemJobObj?.storageBoxes && itemJobObj?.storageBoxes?.length > 0) {
              mutateRemoveIteJobInStorageBox({ itemJobId: itemJobObj?.id });
            }

            if (
              itemJobObj?.mapCells &&
              itemJobObj?.mapCells?.length > 0 &&
              itemJobObj?.mapCells?.[0]?.id
            ) {
              mutateRemoveItemJobInMapCell({ id: itemJobObj?.mapCells?.[0]?.id });
            }
          } else onSubmit({ ...itemJobObj, mapCells: [], storageBoxes: [] });
        } else if (itemJobLocation) {
          if (
            itemJobLocation.mapCellId &&
            itemJobObj.mapCells?.[0]?.id !== itemJobLocation.mapCellId
          ) {
            const mapCell = mapCells?.find((cell) => cell.id === itemJobLocation.mapCellId);
            const storageBox = storageBoxes.find(
              (storageBox) => storageBox.id === itemJobLocation?.storageBoxId
            );

            if (mapCell?.itemsJobs?.length || mapCell?.storageBoxes?.length) {
              let text = "";

              if (mapCell?.itemsJobs?.length) text = mapCell.itemsJobs[0].description ?? "";
              else if (mapCell?.storageBoxes?.length) text = mapCell.storageBoxes[0].number;

              confirmationModalRef?.current?.open(text);
            } else if (itemJobObj.id) {
              handleSubmit();
            } else {
              onSubmit({
                ...itemJobObj,
                mapCells: mapCell ? [mapCell] : [],
                storageBoxes: storageBox ? [storageBox] : [],
              });
            }
          } else if (
            itemJobLocation.storageBoxId &&
            itemJobObj.storageBoxes?.[0]?.id !== itemJobLocation.storageBoxId &&
            canAddItemJobInLocation(itemJobStatus)
          ) {
            if (itemJob?.id !== undefined) {
              moveToStorageBox({
                itemsJobs: [{ id: itemJob?.id }],
                storageBox: { id: itemJobLocation.storageBoxId } as StorageBox,
              });
            } else {
              const storageBox = storageBoxes.find((sb) => sb.id === itemJobLocation?.storageBoxId);

              onSubmit({ ...itemJobObj, storageBoxes: storageBox ? [storageBox] : [] });
            }
          } else {
            onSubmit(itemJobObj);
          }
        }
      }
    }
  };

  const getActionButtonProps = () => {
    if (openOnlyComments) {
      return {
        onCancel: closeModal,
        textCancel: translate("close"),
        customColorCancel: Color.GRAY,
        dataCyCancel: "closeButton",
      } as ActionButtonProps;
    }
    if (uuidItemJob !== undefined) {
      return {
        onSave,
        onCancel: closeModal,
        loading:
          loadingMoveItemJob ||
          loadingMoveItemJobToStorageBox ||
          loadingRemoveItemJobInStorageBox ||
          loadingRemoveItemJobInMapCell,
      } as ActionButtonProps;
    }

    return {
      onCancel: () => {
        if (validateForm(currentStep === Step.DETAILS)) nextStep();
      },
      onSave,
      showCancelButton: currentStep !== Step.CONDITIONS,
      textCancel: translate("next"),
      textSave: translate("create"),
      customColorCancel: Color.SUCCESS_OUTLINED,
      variantCancel: "outlined",
      dataCyCancel: "button-next",
    } as ActionButtonProps;
  };

  const getLeftActionButtonProps = () => {
    if (uuidItemJob === undefined) {
      return {
        showCancelButton: true,
        onCancel: currentStep === Step.DETAILS ? closeModal : previousStep,
        textCancel: currentStep === Step.DETAILS ? translate("cancel") : translate("back"),
        customColorCancel: currentStep === Step.DETAILS ? undefined : Color.DARK,
        dataCyCancel: currentStep === Step.DETAILS ? "button-cancel" : "button-back",
      } as ActionButtonProps;
    }

    return undefined;
  };

  const renderContent = useCallback(() => {
    if (uuidItemJob !== undefined) {
      if (openOnlyComments) {
        return (
          <Details
            itemJobLocation={itemJobLocation}
            itemJobStatus={itemJobStatus}
            setItemJobLocation={setItemJobLocation}
            setItemJobStatus={setItemJobStatus}
            location={location}
            setLocation={(value) => setLocation(value)}
            warehouseList={warehouseList}
            mapCells={mapCells}
            isLoadingWarehouse={isWarehouseListFetching}
            isLoadingMapList={isMapCellsFetching}
            storageBoxes={storageBoxes}
            orderStatus={orderStatus}
            mustBeHardwareBox={mustBeHardwareBox}
            openOnlyComments
          />
        );
      }
      const leftPanels = [
        {
          key: Step.DETAILS,
          menuItem: translate("details"),
          render: () => (
            <Details
              itemJobLocation={itemJobLocation}
              itemJobStatus={itemJobStatus}
              setItemJobLocation={setItemJobLocation}
              setItemJobStatus={setItemJobStatus}
              location={location}
              setLocation={(value) => setLocation(value)}
              warehouseList={warehouseList}
              mapCells={mapCells}
              isLoadingWarehouse={isWarehouseListFetching}
              isLoadingMapList={isMapCellsFetching}
              storageBoxes={storageBoxes}
              orderStatus={orderStatus}
              mustBeHardwareBox={mustBeHardwareBox}
            />
          ),
          hasError: checkFieldsHasError(
            fieldHasError,
            "roomId",
            "description",
            "make",
            "model",
            "serialNumber"
          ),
        },
        {
          key: Step.PHOTOS,
          menuItem: translate("photos"),
          render: () => <Photos />,
        },
        {
          key: Step.CONDITIONS,
          menuItem: translate("itemConditions"),
          render: () => <ItemConditions />,
        },
      ];

      if (itemJob?.item?.mandatoryME || !!itemJob?.isMeCustom) {
        leftPanels.splice(1, 0, {
          key: Step.CHECKLIST,
          menuItem: translate("meChecklist"),
          render: () => <MEChecklist />,
          hasError:
            fieldHasError("meOriginChecklist.meDone") ||
            fieldHasError("meOriginChecklist.meWorking"),
        });
      }

      return (
        <UITabMaterial
          leftPanels={leftPanels}
          selectedLeftPanel={selectedLeftPanel}
          onChangeLeftPanel={(_, value) => setSelectedLeftPanel(Number(value))}
        />
      );
    }

    const steps = [
      {
        key: Step.DETAILS,
        label: translate("details"),
        component: (
          <Details
            itemJobLocation={itemJobLocation}
            itemJobStatus={itemJobStatus}
            setItemJobLocation={setItemJobLocation}
            setItemJobStatus={setItemJobStatus}
            location={location}
            setLocation={(value) => setLocation(value)}
            warehouseList={warehouseList}
            mapCells={mapCells}
            isLoadingWarehouse={isWarehouseListFetching}
            isLoadingMapList={isMapCellsFetching}
            storageBoxes={storageBoxes}
            mustBeHardwareBox={mustBeHardwareBox}
          />
        ),
        required: true,
        hasError: checkFieldsHasError(
          fieldHasError,
          "roomId",
          "description",
          "make",
          "model",
          "serialNumber"
        ),
      },
      {
        key: Step.PHOTOS,
        label: translate("photos"),
        component: <Photos />,
      },
      {
        key: Step.CONDITIONS,
        label: translate("itemConditions"),
        component: <ItemConditions />,
      },
    ];

    if (itemJob?.item?.mandatoryME || !!itemJob?.isMeCustom) {
      steps.splice(1, 0, {
        key: Step.CHECKLIST,
        label: translate("meChecklist"),
        component: <MEChecklist />,
        required: false,
        hasError:
          fieldHasError("meOriginChecklist.meDone") || fieldHasError("meOriginChecklist.meWorking"),
      });
    }

    return <UISteps stepsOption={stepsOption} steps={steps} currentStep={currentStep} />;
  }, [
    itemJob,
    itemJobLocation,
    itemJobStatus,
    currentStep,
    location,
    mapCells,
    storageBoxes,
    warehouseList,
    errors,
    openOnlyComments,
  ]);

  useEffect(() => {
    if (itemJob?.status) setItemJobStatus(itemJob.status);
  }, []);

  useEffect(() => {
    if (itemJobLocation) {
      if (itemJobLocation.mapId) {
        setMapId(itemJobLocation.mapId);
      }
    }
  }, [itemJobLocation]);

  return (
    <Modal
      className={classes.modal}
      open={openModal}
      handleClose={closeModal}
      title={renderModalTitle()}
      showActionButtons
      actionButtonProps={getActionButtonProps()}
      showLeftActionButtons
      actionLeftButtonProps={getLeftActionButtonProps()}
      data-cy="modal-add-actual-item"
      dataCyCloseButton="modal-add-actual-item-close-button"
    >
      {renderContent()}

      <MoveConfirmationModal
        ref={confirmationModalRef}
        onSubmit={handleSubmit}
        isLoading={loadingMoveItemJob}
      />
    </Modal>
  );
};

export default CreateActualItemModal;
